import { cn } from '@/utils/index';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlert, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

const alertVariants = cva('relative w-full rounded-input border p-6 text-text-primary-default', {
  variants: {
    variant: {
      default: 'bg-fill-background-neutral border-outline-neutral',
      danger: 'bg-fill-button-destructive-default border-fill-button-destructive-default text-text-default-inverted',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const Alert = ({
  className,
  variant,
  children,
  onClose,
  icon,
  ...props
}: React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & {
    onClose?: () => void;
    icon?: React.ReactNode;
  }) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'alert',
  });

  return (
    <div role='alert' className={cn(alertVariants({ variant }), className, 'flex items-start gap-1', onClose ? 'pr-14' : 'pr-9')} {...props}>
      <div className='[&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:size-6 [&>svg+div]:translate-y-[-3px] [&>svg~*]:pl-7'>
        <div className='flex items-center gap-4'>
          {icon ?? <CircleAlert className='size-6' />}
          <div>{children}</div>
        </div>
      </div>
      {onClose && (
        <button
          className='rounded-button ring-offset-fill-background-neutral absolute top-3 right-3 block opacity-80 transition-opacity hover:opacity-100 focus:ring-2 focus:outline-hidden disabled:pointer-events-none'
          onClick={onClose}
          type='button'
        >
          <X
            className={cn('stroke-text-default-primary size-5', {
              'stroke-text-default-inverted': variant === 'danger',
            })}
          />
          <span className='sr-only'>{t('closeButton.accessibleText')}</span>
        </button>
      )}
    </div>
  );
};

const AlertTitle = ({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h5 className={cn('text-body-m-semibold mb-1', className)} {...props}>
    {children}
  </h5>
);

const AlertDescription = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <div className={cn('text-body-m-regular', className)} {...props} />
);

export { Alert, AlertDescription, AlertTitle };
